import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import StudentAssignment from '@/components/StudentAssignment';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Play, FileText, Download, Users, BookOpen, X, Upload } from 'lucide-react';
import axios from 'axios';
import { uploadFile, downloadBlob } from '@/lib/uploadFile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isVideo(filename) {
  return /\.(mp4|webm|mov|avi|mkv)$/i.test(filename || '');
}
function isPDF(filename) {
  return /\.pdf$/i.test(filename || '');
}

function TutorialCard({ tutorial, onEdit, onDelete, onAssign, isTeacher, API, token }) {
  const [playing, setPlaying] = useState(false);
  const hasFile = !!tutorial.archivo_filename;
  const ext = tutorial.archivo_original_name?.split('.').pop()?.toLowerCase();

  const handleDownload = async () => {
    try {
      await downloadBlob(tutorial.archivo_filename, tutorial.archivo_original_name, token);
    } catch (err) {
      toast.error(err.message || 'Error al descargar');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col" data-testid={`tutorial-card-${tutorial.id}`}>
      {/* Thumbnail / preview area */}
      <div className="bg-slate-50 border-b relative flex items-center justify-center" style={{ minHeight: 160 }}>
        {hasFile && isVideo(tutorial.archivo_filename) ? (
          playing ? (
            <video
              src={`${API}/files/${tutorial.archivo_filename}`}
              controls autoPlay
              className="w-full max-h-48 object-contain"
              onError={() => toast.error('Error al reproducir el video')}
            />
          ) : (
            <button
              onClick={() => setPlaying(true)}
              className="flex flex-col items-center gap-2 text-slate-400 hover:text-blue-500 transition-colors"
              data-testid={`play-tutorial-${tutorial.id}`}
            >
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                <Play className="w-7 h-7 text-blue-500 ml-1" />
              </div>
              <span className="text-xs">Reproducir video</span>
            </button>
          )
        ) : hasFile && isPDF(tutorial.archivo_filename) ? (
          <button
            onClick={handleDownload}
            className="flex flex-col items-center gap-2 text-slate-400 hover:text-red-500 transition-colors"
            data-testid={`open-pdf-${tutorial.id}`}
          >
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <FileText className="w-7 h-7 text-red-400" />
            </div>
            <span className="text-xs">Descargar PDF</span>
          </button>
        ) : hasFile ? (
          <button
            onClick={handleDownload}
            className="flex flex-col items-center gap-2 text-slate-400 hover:text-green-500 transition-colors"
          >
            <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center">
              <Download className="w-7 h-7 text-green-400" />
            </div>
            <span className="text-xs">{tutorial.archivo_original_name}</span>
          </button>
        ) : (
          <div className="flex flex-col items-center gap-2 text-slate-300">
            <BookOpen className="w-10 h-10" />
            <span className="text-xs">Sin archivo</span>
          </div>
        )}
        {/* Badge asignado */}
        {tutorial.estudiantes_asignados?.length > 0 && (
          <div className="absolute top-2 right-2">
            <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 border-amber-200">
              <Users className="w-3 h-3 mr-1" />
              {tutorial.estudiantes_asignados.length} estudiante(s)
            </Badge>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 flex-1 flex flex-col gap-2">
        <h3 className="font-semibold text-slate-800 text-sm leading-tight">{tutorial.titulo}</h3>
        {tutorial.descripcion && (
          <p className="text-xs text-slate-500 line-clamp-2">{tutorial.descripcion}</p>
        )}
        <p className="text-[10px] text-slate-400 mt-auto">Por {tutorial.autor_name}</p>
      </div>

      {/* Actions (teacher/admin only) */}
      {isTeacher && (
        <div className="border-t px-4 py-2.5 flex items-center gap-2">
          <button
            onClick={() => onAssign(tutorial)}
            className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium"
            data-testid={`assign-tutorial-${tutorial.id}`}
          >
            <Users className="w-3.5 h-3.5" /> Asignar
          </button>
          <button
            onClick={() => onEdit(tutorial)}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium ml-auto"
            data-testid={`edit-tutorial-${tutorial.id}`}
          >
            <Edit2 className="w-3.5 h-3.5" /> Editar
          </button>
          <button
            onClick={() => onDelete(tutorial.id)}
            className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 font-medium"
            data-testid={`delete-tutorial-${tutorial.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Eliminar
          </button>
        </div>
      )}
    </div>
  );
}

function TutorialForm({ initial, classId, onSave, onCancel, uploading, setUploading, token }) {
  const [titulo, setTitulo] = useState(initial?.titulo || '');
  const [descripcion, setDescripcion] = useState(initial?.descripcion || '');
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!titulo.trim()) { toast.error('El título es obligatorio'); return; }
    setUploading(true);
    setUploadProgress(0);
    try {
      let archivo_filename = initial?.archivo_filename || null;
      let archivo_original_name = initial?.archivo_original_name || null;
      let archivo_content_type = initial?.archivo_content_type || null;

      // Upload file via chunked upload (handles large videos without timeout)
      if (file) {
        toast.info('Subiendo archivo...');
        const uploaded = await uploadFile(file, token, (pct) => setUploadProgress(pct));
        archivo_filename = uploaded.filename;
        archivo_original_name = uploaded.original_name || file.name;
        archivo_content_type = file.type || 'application/octet-stream';
      }

      const payload = {
        class_id: classId,
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        archivo_filename,
        archivo_original_name,
        archivo_content_type,
      };

      if (initial) {
        await axios.put(`${API}/tutorials/${initial.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tutorial actualizado');
      } else {
        await axios.post(`${API}/tutorials`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tutorial subido');
      }
      onSave();
    } catch (err) {
      toast.error(err.message || 'Error al guardar el tutorial');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="tutorial-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-bold text-slate-800">{initial ? 'Editar Tutorial' : 'Subir Tutorial'}</h2>
          <button onClick={onCancel} disabled={uploading} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label>Título *</Label>
            <Input data-testid="tutorial-title-input" value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ej: Cómo usar el Present Perfect" />
          </div>
          <div>
            <Label>Descripción (opcional)</Label>
            <textarea
              value={descripcion}
              onChange={e => setDescripcion(e.target.value)}
              placeholder="Breve descripción del tutorial..."
              className="w-full mt-1 px-3 py-2 border rounded-xl text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-blue-300"
              data-testid="tutorial-desc-input"
            />
          </div>
          <div>
            <Label>Archivo / Video {initial?.archivo_filename ? '(dejar vacío para no cambiar)' : ''}</Label>
            <div className="mt-1 flex items-center gap-3">
              <label className="cursor-pointer flex items-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-xl text-sm text-slate-500 hover:border-blue-400 hover:text-blue-500 transition-colors">
                <Upload className="w-4 h-4" />
                {file ? file.name : (initial?.archivo_original_name || 'Seleccionar archivo')}
                <input type="file" className="hidden" accept="video/*,.pdf,.doc,.docx,.ppt,.pptx"
                  onChange={e => setFile(e.target.files?.[0])} data-testid="tutorial-file-input" />
              </label>
              {file && <button type="button" onClick={() => setFile(null)} className="text-slate-400 hover:text-red-400"><X className="w-4 h-4" /></button>}
            </div>
          </div>

          {/* Upload progress bar */}
          {uploading && uploadProgress > 0 && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-slate-500">
                <span>Subiendo archivo...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-[11px] text-slate-400">Los videos grandes se suben en fragmentos. Por favor espera...</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={uploading} className="flex-1">Cancelar</Button>
            <Button type="submit" disabled={uploading} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="tutorial-save-btn">
              {uploading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{uploadProgress > 0 ? `${uploadProgress}%` : 'Procesando...'}</span>
                </div>
              ) : (initial ? 'Guardar cambios' : 'Subir Tutorial')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssignModal({ tutorial, students, onSave, onCancel, token }) {
  const [selected, setSelected] = useState(tutorial.estudiantes_asignados || []);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/tutorials/${tutorial.id}/assign`,
        { student_ids: selected },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(selected.length === 0 ? 'Tutorial visible para todos' : `Asignado a ${selected.length} estudiante(s)`);
      onSave();
    } catch { toast.error('Error al asignar'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-bold text-slate-800">Asignar Estudiantes</h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-slate-500">Selecciona quiénes pueden ver <strong>{tutorial.titulo}</strong>. Si no seleccionas ninguno, será visible para todos.</p>
          <StudentAssignment
            students={students}
            selectedStudents={selected}
            setSelectedStudents={setSelected}
            label="Asignar a Estudiantes"
          />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onCancel} className="flex-1">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" data-testid="confirm-assign-tutorial-btn">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Guardar asignación'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TutorialsTab({ classId, students = [] }) {
  const { user, token } = useAuth();
  const isTeacher = user?.role === 'teacher' || user?.role === 'admin';
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState(null);
  const [assigningTutorial, setAssigningTutorial] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchTutorials = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tutorials/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTutorials(res.data);
    } catch { toast.error('Error al cargar tutoriales'); }
    finally { setLoading(false); }
  }, [classId, token]);

  useEffect(() => { fetchTutorials(); }, [fetchTutorials]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este tutorial?')) return;
    try {
      await axios.delete(`${API}/tutorials/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tutorial eliminado');
      fetchTutorials();
    } catch { toast.error('Error al eliminar'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400">Cargando...</div>;

  return (
    <div className="space-y-6" data-testid="tutorials-tab">
      {/* Header (teacher only) */}
      {isTeacher && (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-700">Gestión de Tutoriales</h3>
            <p className="text-xs text-slate-400">{tutorials.length} tutorial(es) en esta clase</p>
          </div>
          <Button
            onClick={() => { setEditingTutorial(null); setShowForm(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl gap-2"
            data-testid="upload-tutorial-btn"
          >
            <Plus className="w-4 h-4" /> Subir Tutorial
          </Button>
        </div>
      )}

      {/* Gallery */}
      {tutorials.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{isTeacher ? 'No hay tutoriales aún' : 'No tienes tutoriales disponibles'}</p>
          {isTeacher && <p className="text-sm mt-1">Haz clic en "Subir Tutorial" para comenzar</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tutorials.map(tut => (
            <TutorialCard
              key={tut.id}
              tutorial={tut}
              isTeacher={isTeacher}
              API={API}
              token={token}
              onEdit={(t) => { setEditingTutorial(t); setShowForm(true); }}
              onDelete={handleDelete}
              onAssign={(t) => setAssigningTutorial(t)}
            />
          ))}
        </div>
      )}

      {/* Upload/Edit modal */}
      {showForm && (
        <TutorialForm
          initial={editingTutorial}
          classId={classId}
          token={token}
          uploading={uploading}
          setUploading={setUploading}
          onSave={() => { setShowForm(false); fetchTutorials(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Assign modal */}
      {assigningTutorial && (
        <AssignModal
          tutorial={assigningTutorial}
          students={students}
          token={token}
          onSave={() => { setAssigningTutorial(null); fetchTutorials(); }}
          onCancel={() => setAssigningTutorial(null)}
        />
      )}
    </div>
  );
}
